import React, { useEffect, useState } from "react";
import { api, apiErr, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatGs, LOCATION_NAMES } from "@/lib/format";
import { PageHeader, Btn, Modal, Input, StatusPill } from "@/components/ui/kit";
import ImageSearchModal from "@/components/ImageSearchModal";
import { ComboModal } from "@/components/ComboModal";
import { MovementsModal } from "@/components/MovementsModal";
import { usePasteImage, pasteFromClipboard } from "@/lib/pasteImage";
import { ImageCropModal } from "@/components/ImageCropModal";
import { toast } from "sonner";
import { Plus, Minus, Pencil, Search, ArrowLeftRight, AlertTriangle, Image as ImageIcon, Upload, Package, Trash2, FileJson, Loader2, CheckCircle2, Layers, Barcode, ClipboardPaste, Crop, ImageOff, ImagePlus, Truck, History } from "lucide-react";

const SAMPLE_JSON = `[
  { "nombre": "Capuchino Grande", "categoria": "Cafés Calientes", "precio": 22000, "costo": 8000, "stock": 20 },
  { "name": "Jugo de Naranja", "category": "Bebidas", "price": "18.000", "stock_sabornauta": 15, "stock_compunauta": 10 }
]`;

const empty = { name: "", category: "", price: 0, cost: 0, image_url: "", barcode: "", supplier: "", min_stock: 5, unit: "unidad", stock_sabornauta: 0, stock_compunauta: 0, is_active: true };

export default function Inventory() {
  const { location, can } = useAuth();
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([]);
  const [filterCat, setFilterCat] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [noPhoto, setNoPhoto] = useState(false);
  const [search, setSearch] = useState("");
  const [lowOnly, setLowOnly] = useState(false);
  const [editing, setEditing] = useState(null);
  const [imgSearch, setImgSearch] = useState(false);
  const [cropSrc, setCropSrc] = useState(null);
  const [transfer, setTransfer] = useState(null);
  const [tQty, setTQty] = useState(1);
  const [tTo, setTTo] = useState("");
  const [importOpen, setImportOpen] = useState(false);
  const [importText, setImportText] = useState("");
  const [importMode, setImportMode] = useState("sum");
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const [combo, setCombo] = useState(undefined);
  const [moves, setMoves] = useState(null);

  const load = async () => {
    try {
      const params = { location };
      if (filterCat) params.category = filterCat;
      if (filterStatus) params.status = filterStatus;
      if (search) params.search = search;
      const url = lowOnly ? "/products/low-stock" : "/products";
      const { data } = await api.get(url, { params });
      setProducts(data);
    } catch (e) { toast.error(apiErr(e)); }
  };
  useEffect(() => { load(); }, [location, filterCat, filterStatus, search, lowOnly]);
  const missingPhotos = products.filter((p) => !p.image_url).length;
  const shown = noPhoto ? products.filter((p) => !p.image_url) : products;
  useEffect(() => { api.get("/categories").then((r) => setCats(r.data)).catch(() => {}); }, []);

  const adjust = async (p, delta) => {
    try {
      await api.post(`/products/${p.id}/adjust-stock`, { location, delta, reason: "ajuste manual" });
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const save = async () => {
    try {
      const body = { ...editing, price: Number(editing.price), cost: Number(editing.cost), min_stock: Number(editing.min_stock), stock_sabornauta: location === "sabornauta" ? Number(editing.stock_sabornauta) : 0, stock_compunauta: location === "compunauta" ? Number(editing.stock_compunauta) : 0 };
      if (editing.id) await api.put(`/products/${editing.id}`, body);
      else await api.post("/products", body, { params: { location } });
      toast.success("Producto guardado");
      setEditing(null); load();
      api.get("/categories").then((r) => setCats(r.data));
    } catch (e) { toast.error(apiErr(e)); }
  };

  const del = async (p) => {
    if (!window.confirm(`¿Eliminar ${p.name} del inventario de ${LOCATION_NAMES[location]}? (el otro local no se modifica)`)) return;
    try { await api.delete(`/products/${p.id}`, { params: { location } }); toast.success("Eliminado de este local"); load(); } catch (e) { toast.error(apiErr(e)); }
  };

  const setPastedImg = (url) => { setEditing((e) => (e ? { ...e, image_url: url } : e)); setCropSrc(url); };
  usePasteImage(!!editing && !imgSearch && !cropSrc, setPastedImg);

  const uploadImg = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setEditing((e) => ({ ...e, image_url: data.url }));
      setCropSrc(data.url);
      toast.success("Imagen subida");
    } catch (e) { toast.error(apiErr(e)); }
  };

  const doTransfer = async () => {
    try {
      await api.post("/transfers", { product_id: transfer.id, from_location: location, to_location: tTo, quantity: Number(tQty) });
      toast.success("Mercadería transferida");
      setTransfer(null); setTQty(1); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const otherLoc = Object.keys(LOCATION_NAMES).find((l) => l !== location);

  const runImport = async () => {
    let parsed;
    try {
      parsed = JSON.parse(importText);
    } catch {
      toast.error("JSON inválido. Revisá el formato."); return;
    }
    const items = Array.isArray(parsed) ? parsed : (parsed.items || parsed.products || parsed.productos || [parsed]);
    setImporting(true); setImportResult(null);
    try {
      const { data } = await api.post("/products/import", { items, mode: importMode, location });
      setImportResult(data);
      toast.success(`${data.created} creados · ${data.updated} actualizados`);
      load(); api.get("/categories").then((r) => setCats(r.data));
    } catch (e) { toast.error(apiErr(e)); }
    finally { setImporting(false); }
  };

  const onImportFile = (file) => {
    const reader = new FileReader();
    reader.onload = (ev) => setImportText(String(ev.target.result || ""));
    reader.readAsText(file);
  };

  return (
    <div>
      <PageHeader title="Inventario" subtitle={`Stock de ${LOCATION_NAMES[location]}`}>
        <Btn variant={lowOnly ? "danger" : "outline"} onClick={() => setLowOnly(!lowOnly)} data-testid="low-stock-toggle">
          <AlertTriangle className="w-4 h-4" /> Faltantes
        </Btn>
        <Btn variant="outline" onClick={() => { setImportOpen(true); setImportResult(null); }} data-testid="import-json-btn"><FileJson className="w-4 h-4" /> Importar JSON</Btn>
        <Btn variant="outline" onClick={() => setCombo(null)} data-testid="new-combo-btn"><Layers className="w-4 h-4" /> Nuevo Combo</Btn>
        <Btn onClick={() => setEditing({ ...empty })} data-testid="new-product-btn"><Plus className="w-4 h-4" /> Nuevo Producto</Btn>
      </PageHeader>

      <div className="flex gap-2 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-3 text-muted-foreground" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="inv-search" placeholder="Buscar…"
            className="w-full h-10 pl-9 pr-3 rounded-lg bg-secondary border border-border focus:border-primary focus:outline-none" />
        </div>
        <select value={filterCat} onChange={(e) => setFilterCat(e.target.value)} data-testid="inv-cat-filter"
          className="h-10 px-3 rounded-lg bg-secondary border border-border focus:outline-none">
          <option value="">Todas las categorías</option>
          {cats.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} data-testid="inv-status-filter"
          className="h-10 px-3 rounded-lg bg-secondary border border-border focus:outline-none">
          <option value="">Todo stock</option>
          <option value="normal">Normal</option>
          <option value="bajo">Bajo</option>
          <option value="agotado">Agotado</option>
        </select>
        <button onClick={() => setNoPhoto(!noPhoto)} data-testid="inv-nophoto-filter"
          className={`h-10 px-4 rounded-lg text-sm font-semibold border flex items-center gap-2 transition-colors ${noPhoto ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-accent"}`}>
          <ImageOff className="w-4 h-4" /> Sin foto
          <span className={`min-w-[22px] h-5 px-1.5 rounded-full text-[11px] flex items-center justify-center ${noPhoto ? "bg-black/20" : missingPhotos ? "bg-destructive text-white" : "bg-secondary"}`} data-testid="inv-nophoto-count">{missingPhotos}</span>
        </button>
      </div>

      <div className="card-surface overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="inventory-table">
            <thead className="bg-secondary/50 text-muted-foreground text-xs uppercase">
              <tr>
                <th className="text-left p-3 font-semibold">Producto</th>
                <th className="text-left p-3 font-semibold">Categoría</th>
                <th className="text-right p-3 font-semibold">Precio</th>
                <th className="text-center p-3 font-semibold">Stock {LOCATION_NAMES[location]}</th>
                <th className="text-center p-3 font-semibold">Estado</th>
                <th className="text-right p-3 font-semibold">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {shown.length === 0 && (
                <tr><td colSpan={6} className="p-8 text-center text-sm text-muted-foreground" data-testid="inv-empty">{noPhoto ? "Todos los productos tienen foto" : "Sin productos"}</td></tr>
              )}
              {shown.map((p) => (
                <tr key={p.id} className="border-t border-border hover:bg-accent/30" data-testid={`inv-row-${p.id}`}>
                  <td className="p-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-secondary overflow-hidden shrink-0">
                        {p.image_url ? <img src={fileUrl(p.image_url)} alt="" className="w-full h-full object-cover" /> : (
                          <button onClick={() => (p.is_combo ? setCombo(p) : setEditing({ ...p }))} title="Agregar foto" data-testid={`add-photo-${p.id}`}
                            className="w-full h-full flex items-center justify-center border border-dashed border-destructive/60 rounded-lg text-destructive hover:bg-destructive/10"><ImagePlus className="w-4 h-4" /></button>
                        )}
                      </div>
                      <div>
                        <span className="font-medium flex items-center gap-1.5">{p.name}{p.is_combo && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-primary/15 text-primary">COMBO</span>}</span>
                        {p.is_combo ? <p className="text-[11px] text-muted-foreground">{(p.combo_names || []).join(" + ")}</p>
                          : p.barcode && <p className="text-[11px] text-muted-foreground font-data flex items-center gap-1"><Barcode className="w-3 h-3" />{p.barcode}</p>}
                        {p.supplier && <p className="text-[11px] text-muted-foreground flex items-center gap-1" data-testid={`inv-supplier-${p.id}`}><Truck className="w-3 h-3" />{p.supplier}</p>}
                      </div>
                    </div>
                  </td>
                  <td className="p-3 text-muted-foreground">{p.category}</td>
                  <td className="p-3 text-right font-data">{formatGs(p.price)}</td>
                  <td className="p-3">
                    {p.is_combo ? <p className="text-center font-data font-bold" title="Según stock de sus productos">{p[`stock_${location}`]}</p> : (
                    <div className="flex items-center justify-center gap-1.5">
                      <button onClick={() => adjust(p, -1)} data-testid={`dec-${p.id}`} className="w-7 h-7 rounded-md bg-secondary hover:bg-destructive/20 flex items-center justify-center"><Minus className="w-3.5 h-3.5" /></button>
                      <span className="font-data font-bold w-10 text-center">{p[`stock_${location}`]}</span>
                      <button onClick={() => adjust(p, 1)} data-testid={`inc-${p.id}`} className="w-7 h-7 rounded-md bg-secondary hover:bg-emerald-500/20 flex items-center justify-center"><Plus className="w-3.5 h-3.5" /></button>
                    </div>)}
                  </td>
                  <td className="p-3 text-center"><StatusPill status={p.status} /></td>
                  <td className="p-3">
                    <div className="flex items-center justify-end gap-1">
                      {!p.is_combo && <button onClick={() => setMoves(p)} data-testid={`movements-${p.id}`} title="Movimientos" className="w-8 h-8 rounded-md hover:bg-accent flex items-center justify-center text-muted-foreground hover:text-primary"><History className="w-4 h-4" /></button>}
                      {!p.is_combo && <button onClick={() => setTransfer(p) || setTTo(otherLoc)} data-testid={`transfer-${p.id}`} title="Transferir" className="w-8 h-8 rounded-md hover:bg-accent flex items-center justify-center text-muted-foreground hover:text-primary"><ArrowLeftRight className="w-4 h-4" /></button>}
                      <button onClick={() => (p.is_combo ? setCombo(p) : setEditing({ ...p }))} data-testid={`edit-${p.id}`} title="Editar" className="w-8 h-8 rounded-md hover:bg-accent flex items-center justify-center text-muted-foreground hover:text-primary"><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => del(p)} data-testid={`del-${p.id}`} title="Eliminar" className="w-8 h-8 rounded-md hover:bg-accent flex items-center justify-center text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {products.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">Sin productos</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit/Create modal */}
      <Modal open={!!editing} onClose={() => setEditing(null)} title={editing?.id ? "Editar producto" : "Nuevo producto"} wide top>
        {editing && (
          <div className="space-y-4">
            <div className="flex gap-4">
              <div className="w-28 shrink-0 space-y-1.5">
                <div className="w-28 h-[84px] rounded-lg bg-secondary overflow-hidden border border-border">
                  {editing.image_url ? <img src={fileUrl(editing.image_url)} alt="" className="w-full h-full object-cover" data-testid="prod-image-preview" /> : <ImageIcon className="w-8 h-8 m-7 text-muted-foreground" />}
                </div>
                {editing.image_url && <button type="button" onClick={() => setCropSrc(editing.image_url)} data-testid="crop-image-btn" className="w-full h-7 rounded-md border border-border text-xs font-semibold hover:bg-accent flex items-center justify-center gap-1"><Crop className="w-3 h-3" /> Recortar</button>}
              </div>
              <div className="flex-1 space-y-2">
                <Btn variant="outline" type="button" onClick={() => setImgSearch(true)} data-testid="search-image-btn" className="w-full"><Search className="w-4 h-4" /> Buscar en internet</Btn>
                <Btn variant="outline" type="button" onClick={() => pasteFromClipboard(setPastedImg)} data-testid="paste-image-btn" className="w-full"><ClipboardPaste className="w-4 h-4" /> Pegar imagen copiada</Btn>
                <label className="block">
                  <Btn variant="outline" type="button" className="w-full" onClick={(e) => e.currentTarget.parentElement.querySelector("input").click()}><Upload className="w-4 h-4" /> Subir imagen</Btn>
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && uploadImg(e.target.files[0])} data-testid="upload-image-input" />
                </label>
                <p className="text-[10px] text-muted-foreground">Copiá una imagen (clic derecho → Copiar imagen) y presioná Ctrl+V.</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><label className="text-sm font-medium mb-1 block">Nombre</label><Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="prod-name" /></div>
              <div><label className="text-sm font-medium mb-1 block">Categoría</label><Input value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} list="cats" data-testid="prod-cat" /><datalist id="cats">{cats.map((c) => <option key={c} value={c} />)}</datalist></div>
              <div><label className="text-sm font-medium mb-1 block">Unidad</label><Input value={editing.unit} onChange={(e) => setEditing({ ...editing, unit: e.target.value })} /></div>
              <div className="col-span-2"><label className="text-sm font-medium mb-1 block">Código de barras</label><Input value={editing.barcode || ""} onChange={(e) => setEditing({ ...editing, barcode: e.target.value })} data-testid="prod-barcode" placeholder="Escaneá o escribí el código" /></div>
              <div><label className="text-sm font-medium mb-1 block">Precio venta (Gs.)</label><Input type="number" value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} data-testid="prod-price" /></div>
              <div><label className="text-sm font-medium mb-1 block">Costo (Gs.)</label><Input type="number" value={editing.cost} onChange={(e) => setEditing({ ...editing, cost: e.target.value })} /></div>
              <div><label className="text-sm font-medium mb-1 block">Stock mínimo</label><Input type="number" value={editing.min_stock} onChange={(e) => setEditing({ ...editing, min_stock: e.target.value })} /></div>
              <div className="col-span-2"><label className="text-sm font-medium mb-1 block">Proveedor habitual</label><Input list="supplier-list" value={editing.supplier || ""} onChange={(e) => setEditing({ ...editing, supplier: e.target.value })} data-testid="prod-supplier" placeholder="Ej: Distribuidora Lácteos" />
                <datalist id="supplier-list">{[...new Set(products.map((p) => p.supplier).filter(Boolean))].map((s) => <option key={s} value={s} />)}</datalist></div>
              {editing.id ? (
                <div className="col-span-2 text-xs text-muted-foreground rounded-lg bg-secondary p-3" data-testid="prod-stock-note">Stock en {LOCATION_NAMES[location]}: <b className="text-foreground font-data">{editing[`stock_${location}`] || 0}</b>. Se modifica con Compras, Transferencias o los botones +/−.</div>
              ) : (
                <div className="col-span-2"><label className="text-sm font-medium mb-1 block">Stock inicial en {LOCATION_NAMES[location]}</label><Input type="number" value={editing[`stock_${location}`]} onChange={(e) => setEditing({ ...editing, [`stock_${location}`]: e.target.value })} data-testid="prod-initial-stock" /></div>
              )}
            </div>
            <div className="flex gap-2">
              <Btn variant="outline" onClick={() => setEditing(null)} className="flex-1">Cancelar</Btn>
              <Btn onClick={save} data-testid="save-product" className="flex-1">Guardar</Btn>
            </div>
          </div>
        )}
      </Modal>

      <ImageSearchModal open={imgSearch} onClose={() => setImgSearch(false)} defaultQuery={editing?.name || ""} barcode={editing?.barcode || ""} onSelect={(url) => { setEditing((e) => ({ ...e, image_url: url })); setCropSrc(url); }} />
      <ImageCropModal src={cropSrc} onClose={() => setCropSrc(null)} onDone={(url) => { setEditing((e) => (e ? { ...e, image_url: url } : e)); setCropSrc(null); }} />

      <MovementsModal product={moves} onClose={() => setMoves(null)} />
      <ComboModal open={combo !== undefined} combo={combo} onClose={() => setCombo(undefined)} onSaved={() => { setCombo(undefined); load(); }} />

      {/* Transfer modal */}
      <Modal open={!!transfer} onClose={() => setTransfer(null)} title="Transferir mercadería entre locales">
        {transfer && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">Producto: <span className="font-semibold text-foreground">{transfer.name}</span></p>
            <div className="flex items-center justify-center gap-3 text-sm">
              <span className="px-3 py-2 rounded-lg bg-secondary font-medium">{LOCATION_NAMES[location]} ({transfer[`stock_${location}`]})</span>
              <ArrowLeftRight className="w-5 h-5 text-primary" />
              <span className="px-3 py-2 rounded-lg bg-secondary font-medium">{LOCATION_NAMES[otherLoc]} ({transfer[`stock_${otherLoc}`]})</span>
            </div>
            <div><label className="text-sm font-medium mb-1 block">Cantidad a transferir</label><Input type="number" value={tQty} onChange={(e) => setTQty(e.target.value)} data-testid="transfer-qty" /></div>
            <div className="flex gap-2">
              <Btn variant="outline" onClick={() => setTransfer(null)} className="flex-1">Cancelar</Btn>
              <Btn onClick={doTransfer} data-testid="confirm-transfer" className="flex-1">Transferir</Btn>
            </div>
          </div>
        )}
      </Modal>

      {/* Import JSON modal */}
      <Modal open={importOpen} onClose={() => setImportOpen(false)} title="Importar productos desde JSON" wide top>
        <div className="space-y-4">
          <div className="text-sm text-muted-foreground">
            Pegá un arreglo JSON o subí un archivo <code className="text-primary">.json</code>. El sistema reconoce automáticamente campos como <b>nombre/name</b>, <b>precio/price</b>, <b>costo/cost</b>, <b>categoria/category</b>, <b>stock</b>, <b>imagen/image</b>, <b>barcode/código de barras</b>, etc. Los productos existentes (por código de barras o nombre) se actualizan; los marcados como eliminados se omiten.
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <label>
              <Btn variant="outline" type="button" onClick={(e) => e.currentTarget.parentElement.querySelector("input").click()}><Upload className="w-4 h-4" /> Subir archivo .json</Btn>
              <input type="file" accept=".json,application/json" className="hidden" onChange={(e) => e.target.files[0] && onImportFile(e.target.files[0])} data-testid="import-file-input" />
            </label>
            <Btn variant="ghost" type="button" onClick={() => setImportText(SAMPLE_JSON)} data-testid="import-sample">Ver ejemplo</Btn>
          </div>
          <textarea value={importText} onChange={(e) => setImportText(e.target.value)} data-testid="import-textarea"
            placeholder={SAMPLE_JSON} rows={9}
            className="w-full rounded-lg bg-secondary border border-border focus:border-primary focus:outline-none p-3 font-data text-xs" />
          <div>
            <label className="text-sm font-medium mb-1.5 block">Stock de productos ya existentes</label>
            <div className="grid grid-cols-3 gap-2">
              {[["sum", "Sumar"], ["replace", "Reemplazar"], ["skip", "No tocar"]].map(([v, l]) => (
                <button key={v} onClick={() => setImportMode(v)} data-testid={`import-mode-${v}`}
                  className={`h-10 rounded-lg text-sm font-medium border transition-colors ${importMode === v ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-accent"}`}>{l}</button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-1.5">Stock asignado al local activo: <b className="text-foreground">{LOCATION_NAMES[location]}</b> (salvo que el JSON indique stock por local).</p>
          </div>

          {importResult && (
            <div className="rounded-lg border border-border p-4 space-y-2" data-testid="import-result">
              <div className="flex items-center gap-4 text-sm font-semibold">
                <span className="flex items-center gap-1.5 text-emerald-500"><CheckCircle2 className="w-4 h-4" /> {importResult.created} creados</span>
                <span className="flex items-center gap-1.5 text-primary"><CheckCircle2 className="w-4 h-4" /> {importResult.updated} actualizados</span>
                {importResult.errors?.length > 0 && <span className="flex items-center gap-1.5 text-destructive"><AlertTriangle className="w-4 h-4" /> {importResult.errors.length} con error</span>}
              </div>
              {importResult.errors?.length > 0 && (
                <ul className="text-xs text-destructive space-y-0.5 max-h-24 overflow-y-auto">
                  {importResult.errors.map((er, i) => <li key={i}>Fila {er.row}: {er.error}</li>)}
                </ul>
              )}
            </div>
          )}

          <div className="flex gap-2">
            <Btn variant="outline" onClick={() => setImportOpen(false)} className="flex-1">Cerrar</Btn>
            <Btn onClick={runImport} disabled={importing || !importText.trim()} data-testid="run-import" className="flex-1">
              {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileJson className="w-4 h-4" />} Importar
            </Btn>
          </div>
        </div>
      </Modal>
    </div>
  );
}
